import type { QueryClient } from '@tanstack/react-query';
import type { User } from '@socialsoup/shared';
import { isCurrentAuthAttempt, setRefreshToken } from '@/lib/auth';
import { useAuthStore } from '@/stores/authStore';

/** What a successful sign-in returns: password login and the SSO exchange share this shape. */
export interface SignInSession {
  user: User;
  accessToken: string;
  refreshToken: string;
}

/**
 * Commit a sign-in's session — the one copy shared by password login (`useAuth().login`) and
 * SSO (`SsoCompletePage`). sso-login Phase 4 moved it here unchanged from `useAuth`'s
 * `onSuccess`; each caller still owns where it navigates afterwards. `MagicLoginPage` keeps
 * its own inline copy (out of that phase's scope).
 *
 * `attempt` is the epoch the caller took from `beginAuthAttempt()` BEFORE its request
 * (theme-customization A3). Returns `false`, having written nothing, when a newer attempt or a
 * logout has since taken over; the caller must then not navigate either.
 */
export function completeSignIn(
  session: SignInSession,
  attempt: number,
  queryClient: Pick<QueryClient, 'clear'>,
): boolean {
  // A STALE SUCCESS COMMITS NOTHING — not the refresh token, not the session, not the cache
  // clear, not the navigation. Writing only "some" of those would be worse than writing all
  // of them: a refresh token for an account nobody is signed into is a credential with no
  // owner, and a navigation would move a user who has already gone somewhere else.
  if (!isCurrentAuthAttempt(attempt)) return false;
  setRefreshToken(session.refreshToken, session.user.id);
  useAuthStore.getState().beginSession(session.user, session.accessToken);
  queryClient.clear();
  return true;
}
